import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, Clock8, ArrowRight } from 'lucide-react';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';

/**
 * Landing page for invoice-only accounts (see isInvoiceOnlyAccount): a short
 * welcome plus a card per destination they have — upload invoices and review the
 * orders those invoices produced. Gated to invoice-only accounts by
 * InvoiceOnlyRoute; everyone else is sent to the dashboard.
 */
const HOME_CARDS = [
  {
    to: '/invoices',
    Icon: FileText,
    titleKey: 'home.uploadInvoices.title',
    descriptionKey: 'home.uploadInvoices.description',
    actionKey: 'home.uploadInvoices.action',
  },
  {
    to: '/orderhistory/orders',
    Icon: Clock8,
    titleKey: 'home.orders.title',
    descriptionKey: 'home.orders.description',
    actionKey: 'home.orders.action',
  },
];

const HomePage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <MobilePageHeader title={t('nav.homePage')} />
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="hidden lg:block text-3xl font-bold text-textPrimary dark:text-textPrimary-dark">
          {t('home.title')}
        </h1>
        <p className="text-muted dark:text-muted-dark mt-2 mb-6">{t('home.subtitle')}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {HOME_CARDS.map(({ to, Icon, titleKey, descriptionKey, actionKey }) => (
            <Link
              key={to}
              to={to}
              className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-5 hover:shadow-md transition flex flex-col"
            >
              <div className="flex items-center gap-3 mb-2">
                <Icon className="w-6 h-6 text-textGreen dark:text-textGreen-dark" />
                <h2 className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">
                  {t(titleKey)}
                </h2>
              </div>
              <p className="text-sm text-muted dark:text-muted-dark">{t(descriptionKey)}</p>
              <span className="mt-4 flex items-center gap-1 text-sm font-medium text-textGreen dark:text-textGreen-dark">
                {t(actionKey)}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

export default HomePage;
